// ============================================================
// bandwidthProbe — saturating throughput measurement for LL-HLS
// ============================================================
//
// At the live edge an LL-HLS client only ever downloads ~400 ms parts, a few
// hundred KB each, spaced by the part duration. That uses a fraction of the
// pipe, so no matter how the samples are processed they cannot demonstrate more
// than a fraction of the real capacity — which is why a 30 Mbit/s connection can
// sit on 720p forever while the 1080p rung needs a 14 Mbit/s proof.
//
// The probe fetches the head of one already-published complete segment of the
// candidate rendition and times only the body transfer. That saturates the link
// for a few hundred milliseconds and produces an honest ceiling, at the cost of
// `maxBytes` of traffic per probe (rate-limited by AbrPolicy.probeCooldownMs).

export interface ProbeResult {
  bytes: number;
  /** First byte received (performance clock) — excludes TTFB and blocking hold. */
  startMs: number;
  /** Last byte received (performance clock). */
  endMs: number;
}

export interface ProbeOptions {
  /** Level playlist URL, already carrying CDN auth params. */
  playlistUrl: string;
  /** Appends CDN auth params to a derived segment URL. */
  withAuth: (url: string) => string;
  /** Stop reading after this many bytes. */
  maxBytes?: number;
  /** Give up on the whole probe after this long. */
  timeoutMs?: number;
  fetchImpl?: typeof fetch;
  now?: () => number;
}

const DEFAULT_MAX_BYTES = 512 * 1024;
const DEFAULT_TIMEOUT_MS = 4_000;
/**
 * A transfer shorter than this cannot be timed well enough to trust. Kept low:
 * 512 KB on a 270 Mbit/s link lands in ~15 ms, and a higher floor threw away the
 * measurement precisely on the connections that had the most headroom to prove.
 */
const MIN_USEFUL_TRANSFER_MS = 5;
/** A transfer smaller than this is dominated by connection ramp-up. */
const MIN_USEFUL_BYTES = 64 * 1024;

/**
 * Picks the newest complete segment from a media playlist. Plain URI lines are
 * complete segments; LL-HLS parts only ever appear inside `#EXT-X-PART` tags, so
 * they are skipped by construction — a part would measure production pacing
 * rather than network capacity.
 */
export function pickProbeSegment(
  playlist: string,
  playlistUrl: string,
): string | null {
  const lines = playlist.split('\n');
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i].trim();
    if (!line || line.startsWith('#')) continue;
    try {
      return new URL(line, playlistUrl).toString();
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Measures body-transfer throughput of one segment of `playlistUrl`.
 * Resolves to null when the probe could not produce a usable measurement.
 */
export async function probeThroughput(
  options: ProbeOptions,
): Promise<ProbeResult | null> {
  const {
    playlistUrl,
    withAuth,
    maxBytes = DEFAULT_MAX_BYTES,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    fetchImpl = typeof fetch === 'function' ? fetch : undefined,
    now = () => performance.now(),
  } = options;

  if (!fetchImpl) return null;

  const controller =
    typeof AbortController === 'function' ? new AbortController() : null;
  const timer = setTimeout(() => controller?.abort(), timeoutMs);

  try {
    const playlistRes = await fetchImpl(playlistUrl, {
      signal: controller?.signal,
    });
    if (!playlistRes.ok) return null;
    const segmentUrl = pickProbeSegment(await playlistRes.text(), playlistUrl);
    if (!segmentUrl) return null;

    const res = await fetchImpl(withAuth(segmentUrl), {
      signal: controller?.signal,
    });
    if (!res.ok) return null;

    // Headers are in: everything from here on is body bytes on the wire.
    const startMs = now();
    let bytes = 0;
    let endMs = startMs;

    const reader = res.body?.getReader?.();
    if (reader) {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        bytes += value?.byteLength ?? 0;
        endMs = now();
        if (bytes >= maxBytes) {
          void reader.cancel().catch(() => {});
          break;
        }
      }
    } else {
      // No streaming body (older platforms, test doubles): time the whole read.
      bytes = (await res.arrayBuffer()).byteLength;
      endMs = now();
    }

    if (bytes < MIN_USEFUL_BYTES) return null;
    if (endMs - startMs < MIN_USEFUL_TRANSFER_MS) return null;
    return { bytes, startMs, endMs };
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}
